"use client";

import { c as _c } from "react/compiler-runtime";
import { format } from "date-fns";
import { Plus, Loader2, MessageSquare, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { List, type RowComponentProps } from "react-window";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useApi } from "@/lib/fetch-client";
import { cn } from "@/lib/utils";
import { useI18n } from "@llmgateway/i18n/react";
import type { Chat } from "@/hooks/useChats";
import type { TranslationValues } from "@llmgateway/i18n";
const PAGE_SIZE = 50;
const ROW_HEIGHT_ACTION = 44;
const ROW_HEIGHT_HEADER = 38;
const ROW_HEIGHT_CHAT = 44;
const ROW_HEIGHT_EMPTY = 180;
const ROW_HEIGHT_LOADER = 48;

// How long to wait for the dialog close animation before navigating.
// Should match your Dialog's CSS exit animation duration.
const CLOSE_ANIMATION_MS = 150;
type SearchRow = {
  type: "action";
  key: string;
} | {
  type: "header";
  key: string;
  title: string;
} | {
  type: "chat";
  key: string;
  chat: Chat;
} | {
  type: "empty";
  key: string;
  message: string;
} | {
  type: "loader";
  key: string;
  message: string;
};
interface ChatSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNewChat?: () => void;
  onChatSelect?: (chatId: string) => void;
}
interface SearchRowProps {
  rows: SearchRow[];
  onNewChat: () => void;
  onChatSelect: (chatId: string) => void;
  t: Translate;
}
type Translate = (key: string, values?: TranslationValues) => string;
function useDebouncedValue(value, delay) {
  const $ = _c(4);
  const [debouncedValue, setDebouncedValue] = useState(value);
  let t0;
  let t1;
  if ($[0] !== delay || $[1] !== value) {
    t0 = () => {
      const timeout = window.setTimeout(() => setDebouncedValue(value), delay);
      return () => window.clearTimeout(timeout);
    };
    t1 = [delay, value];
    $[0] = delay;
    $[1] = value;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return debouncedValue;
}
function formatDate(dateString: string, t: Translate): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  if (diffInHours < 1) {
    return t("time.justNow");
  }
  if (diffInHours < 24) {
    return t("time.hoursAgo", {
      count: Math.floor(diffInHours)
    });
  }
  if (diffInHours < 48) {
    return t("time.yesterday");
  }
  return format(date, "MMM d");
}

// `now` is passed in so the caller controls the reference point,
// avoiding silent bugs if the component is rendered across midnight.
function groupChatsByDate(chats: Chat[], now: Date) {
  const today = new Date(now);
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(now);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const groups = {
    today: [] as Chat[],
    yesterday: [] as Chat[],
    lastWeek: [] as Chat[],
    older: [] as Chat[]
  };
  chats.forEach(chat => {
    const chatDate = new Date(chat.updatedAt);
    if (chatDate.toDateString() === today.toDateString()) {
      groups.today.push(chat);
    } else if (chatDate.toDateString() === yesterday.toDateString()) {
      groups.yesterday.push(chat);
    } else if (chatDate >= lastWeek) {
      groups.lastWeek.push(chat);
    } else {
      groups.older.push(chat);
    }
  });
  return groups;
}
function getSearchRowHeight(index: number, {
  rows
}: SearchRowProps) {
  const row = rows[index];
  if (row?.type === "action") {
    return ROW_HEIGHT_ACTION;
  }
  if (row?.type === "header") {
    return ROW_HEIGHT_HEADER;
  }
  if (row?.type === "loader") {
    return ROW_HEIGHT_LOADER;
  }
  if (row?.type === "empty") {
    return ROW_HEIGHT_EMPTY;
  }
  return ROW_HEIGHT_CHAT;
}
function SearchRowComponent(t0) {
  const $ = _c(51);
  const {
    ariaAttributes,
    index,
    style,
    rows,
    onNewChat,
    onChatSelect,
    t
  } = t0;
  const row = rows[index];
  if (!row) {
    return null;
  }
  if (row.type === "action") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Plus className="size-4 shrink-0" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== t) {
      t2 = t("playground.newChat");
      $[1] = t;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== t2) {
      t3 = <span>{t2}</span>;
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== onNewChat || $[6] !== t3) {
      t4 = <div className="px-2"><button type="button" onClick={onNewChat} className="flex h-11 w-full items-center gap-3 rounded-md px-4 text-left text-sm font-semibold text-foreground transition-colors hover:bg-muted/60">{t1}{t3}</button></div>;
      $[5] = onNewChat;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[8] !== ariaAttributes || $[9] !== style || $[10] !== t4) {
      t5 = <div {...ariaAttributes} style={style}>{t4}</div>;
      $[8] = ariaAttributes;
      $[9] = style;
      $[10] = t4;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    return t5;
  }
  if (row.type === "header") {
    let t1;
    if ($[12] !== row.title) {
      t1 = <div className="flex h-full items-end px-6 pb-2 text-xs font-medium text-muted-foreground">{row.title}</div>;
      $[12] = row.title;
      $[13] = t1;
    } else {
      t1 = $[13];
    }
    let t2;
    if ($[14] !== ariaAttributes || $[15] !== style || $[16] !== t1) {
      t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
      $[14] = ariaAttributes;
      $[15] = style;
      $[16] = t1;
      $[17] = t2;
    } else {
      t2 = $[17];
    }
    return t2;
  }
  if (row.type === "loader") {
    let t1;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Loader2 className="mr-2 size-4 animate-spin" />;
      $[18] = t1;
    } else {
      t1 = $[18];
    }
    let t2;
    if ($[19] !== row.message) {
      t2 = <div className="flex h-full items-center justify-center text-sm text-muted-foreground">{t1}{row.message}</div>;
      $[19] = row.message;
      $[20] = t2;
    } else {
      t2 = $[20];
    }
    let t3;
    if ($[21] !== ariaAttributes || $[22] !== style || $[23] !== t2) {
      t3 = <div {...ariaAttributes} style={style}>{t2}</div>;
      $[21] = ariaAttributes;
      $[22] = style;
      $[23] = t2;
      $[24] = t3;
    } else {
      t3 = $[24];
    }
    return t3;
  }
  if (row.type === "empty") {
    let t1;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <MessageSquare className="mb-3 size-8 opacity-60" />;
      $[25] = t1;
    } else {
      t1 = $[25];
    }
    let t2;
    if ($[26] !== row.message) {
      t2 = <div className="flex h-full flex-col items-center justify-center px-6 text-center text-sm text-muted-foreground">{t1}{row.message}</div>;
      $[26] = row.message;
      $[27] = t2;
    } else {
      t2 = $[27];
    }
    let t3;
    if ($[28] !== ariaAttributes || $[29] !== style || $[30] !== t2) {
      t3 = <div {...ariaAttributes} style={style}>{t2}</div>;
      $[28] = ariaAttributes;
      $[29] = style;
      $[30] = t2;
      $[31] = t3;
    } else {
      t3 = $[31];
    }
    return t3;
  }
  let t1;
  if ($[32] !== onChatSelect || $[33] !== row.chat.id) {
    t1 = () => onChatSelect(row.chat.id);
    $[32] = onChatSelect;
    $[33] = row.chat.id;
    $[34] = t1;
  } else {
    t1 = $[34];
  }
  let t2;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <MessageSquare className="size-4 shrink-0 text-muted-foreground" />;
    $[35] = t2;
  } else {
    t2 = $[35];
  }
  let t3;
  if ($[36] !== row.chat.title) {
    t3 = <span className="min-w-0 flex-1 truncate font-medium">{row.chat.title}</span>;
    $[36] = row.chat.title;
    $[37] = t3;
  } else {
    t3 = $[37];
  }
  let t4;
  if ($[38] !== row.chat.updatedAt || $[39] !== t) {
    t4 = formatDate(row.chat.updatedAt, t);
    $[38] = row.chat.updatedAt;
    $[39] = t;
    $[40] = t4;
  } else {
    t4 = $[40];
  }
  let t5;
  if ($[41] !== t4) {
    t5 = <span className="shrink-0 text-xs text-muted-foreground">{t4}</span>;
    $[41] = t4;
    $[42] = t5;
  } else {
    t5 = $[42];
  }
  let t6;
  if ($[43] !== t1 || $[44] !== t3 || $[45] !== t5) {
    t6 = <div className="px-4"><button type="button" onClick={t1} className="flex h-11 w-full items-center gap-3 rounded-md px-2 text-left text-sm transition-colors hover:bg-muted/60">{t2}{t3}{t5}</button></div>;
    $[43] = t1;
    $[44] = t3;
    $[45] = t5;
    $[46] = t6;
  } else {
    t6 = $[46];
  }
  let t7;
  if ($[47] !== ariaAttributes || $[48] !== style || $[49] !== t6) {
    t7 = <div {...ariaAttributes} style={style}>{t6}</div>;
    $[47] = ariaAttributes;
    $[48] = style;
    $[49] = t6;
    $[50] = t7;
  } else {
    t7 = $[50];
  }
  return t7;
}
export function ChatSearchDialog({
  open,
  onOpenChange,
  onNewChat,
  onChatSelect
}: ChatSearchDialogProps) {
  const {
    t
  } = useI18n();
  const api = useApi();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 250);

  // Stable reference point for date grouping, captured when the dialog opens.
  // Prevents silent mis-grouping if the component renders across midnight.
  const nowRef = useRef<Date>(new Date());

  // Fix: reset query and refresh the date reference when the dialog opens/closes.
  useEffect(() => {
    if (open) {
      nowRef.current = new Date();
    } else {
      setQuery("");
    }
  }, [open]);
  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = api.useInfiniteQuery("get", "/chats/search", {
    params: {
      query: {
        q: debouncedQuery,
        limit: PAGE_SIZE
      }
    }
  }, {
    enabled: open,
    initialPageParam: 0,
    pageParamName: "offset",
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((total, page) => total + (page?.chats.length ?? 0), 0);
      return loaded < (lastPage?.total ?? 0) ? loaded : undefined;
    }
  });
  useEffect(() => {
    if (!open) {
      return;
    }
    const timeout = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timeout);
  }, [open]);
  const chats = useMemo(() => data?.pages.flatMap(page_0 => page_0?.chats ?? []) ?? [], [data]);
  const rows = useMemo<SearchRow[]>(() => {
    const groups = groupChatsByDate(chats, nowRef.current);
    const nextRows: SearchRow[] = [];
    if (!debouncedQuery.trim()) {
      nextRows.push({
        type: "action",
        key: "new-chat"
      });
    }
    [{
      title: t("playground.today"),
      chats: groups.today
    }, {
      title: t("playground.yesterday"),
      chats: groups.yesterday
    }, {
      title: t("playground.last7Days"),
      chats: groups.lastWeek
    }, {
      title: t("playground.older"),
      chats: groups.older
    }].forEach(({
      title,
      chats: groupedChats
    }) => {
      if (groupedChats.length === 0) {
        return;
      }
      nextRows.push({
        type: "header",
        key: `header-${title}`,
        title
      });
      groupedChats.forEach(chat => {
        nextRows.push({
          type: "chat",
          key: `chat-${chat.id}`,
          chat
        });
      });
    });
    if (isLoading) {
      nextRows.push({
        type: "loader",
        key: "loader",
        message: t("search.searchingChats")
      });
    } else if (chats.length === 0) {
      nextRows.push({
        type: "empty",
        key: "empty",
        message: debouncedQuery.trim() ? t("search.noMatchingChats") : t("search.noChatsYet")
      });
    } else if (hasNextPage) {
      nextRows.push({
        type: "loader",
        key: "loader",
        message: t("search.loadingMoreChats")
      });
    }
    return nextRows;
  }, [chats, debouncedQuery, hasNextPage, isLoading, t]);

  // Fix: delay navigation until after the dialog close animation completes,
  // preventing the dialog from flashing back open during the route transition.
  // Applied to both handlers for consistency.
  const closeAndNewChat = useCallback(() => {
    onOpenChange(false);
    setTimeout(() => {
      onNewChat?.();
    }, CLOSE_ANIMATION_MS);
  }, [onNewChat, onOpenChange]);
  const closeAndSelectChat = useCallback((chatId: string) => {
    onOpenChange(false);
    setTimeout(() => {
      onChatSelect?.(chatId);
    }, CLOSE_ANIMATION_MS);
  }, [onChatSelect, onOpenChange]);
  const rowProps = useMemo<SearchRowProps>(() => ({
    rows,
    onNewChat: closeAndNewChat,
    onChatSelect: closeAndSelectChat,
    t
  }), [closeAndNewChat, closeAndSelectChat, rows, t]);

  // Fix: use a ref latch to prevent duplicate fetchNextPage calls that could
  // occur if react-window fires onRowsRendered multiple times before the
  // previous fetch resolves.
  const isFetchingRef = useRef(false);
  useEffect(() => {
    if (!isFetchingNextPage) {
      isFetchingRef.current = false;
    }
  }, [isFetchingNextPage]);
  const handleRowsRendered = useCallback(({
    stopIndex
  }: {
    startIndex: number;
    stopIndex: number;
  }) => {
    if (hasNextPage && !isLoading && !isFetchingRef.current && stopIndex >= rows.length - 4) {
      isFetchingRef.current = true;
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isLoading, rows.length]);
  return <Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent showCloseButton={false} className="grid h-[min(76vh,560px)] max-w-[min(720px,calc(100vw-2rem))] grid-rows-[4rem_minmax(0,1fr)] gap-0 overflow-hidden rounded-xl border-border bg-popover p-0 text-popover-foreground">
				<DialogTitle className="sr-only">
					{t("playground.searchChats")}
				</DialogTitle>
				<DialogDescription className="sr-only">
					{t("search.searchChatsDescription")}
				</DialogDescription>
				<div className="flex h-16 items-center gap-3 border-b px-6">
					<Search className="size-4 shrink-0 text-muted-foreground" />
					<Input ref={inputRef} value={query} onChange={event => setQuery(event.target.value)} placeholder={t("search.searchChatsPlaceholder")} className="h-full border-0 bg-transparent! px-0 text-base shadow-none focus-visible:ring-0" />
					<button type="button" aria-label={t("search.closeSearch")} onClick={() => onOpenChange(false)} className="rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground">
						<X className="size-5" aria-hidden="true" />
					</button>
				</div>
				<div className="min-h-0 flex-1 py-2">
					<List className={cn("h-full min-h-0 w-full")} style={{
          height: "100%",
          width: "100%"
        }} rowComponent={SearchRowComponent} rowCount={rows.length} rowHeight={getSearchRowHeight} rowProps={rowProps} overscanCount={8} onRowsRendered={handleRowsRendered} />
				</div>
			</DialogContent>
		</Dialog>;
}